/**
 * FoodPulseAbout — the /food-pulse/about landing page.
 *
 * Long-form mission / vision / ecosystem article. Content is authored
 * copy (not AI). Section anchors match the About sub-nav in
 * `foodPulseSections.js` (#mission, #team, #partners, #volunteer,
 * #advertise, #privacy, #terms) so the dropdown links deep-scroll to
 * the right spot.
 */
import { Link } from "react-router-dom";
import {
  HeartHandshake, Sparkles, Globe, Tractor, Fish, Truck, Store, Utensils,
  Hotel, ChefHat, GraduationCap, Building2, ShoppingCart, Users, Leaf,
  ArrowRight, Recycle,
} from "lucide-react";

import SeoHead from "@/components/SeoHead";
import FoodPulseSlogan from "@/components/FoodPulseSlogan";
import { FP_SLOGANS } from "@/config/foodPulseSlogans";

// Ecosystem participants — a wide grid on the "Connecting the Entire
// Food Chain" section. Each entry is one node in the food network.
const ECOSYSTEM = [
  { label: "Farmers & Growers",     icon: Tractor },
  { label: "Orchards & Vineyards",  icon: Leaf },
  { label: "Fisheries & Aquaculture", icon: Fish },
  { label: "Dairy Farms",           icon: Tractor },
  { label: "Food Producers",        icon: Building2 },
  { label: "Food Processors",       icon: Building2 },
  { label: "Wholesalers",           icon: Truck },
  { label: "Importers & Exporters", icon: Globe },
  { label: "Distribution Centers",  icon: Building2 },
  { label: "Transportation",        icon: Truck },
  { label: "Grocery Stores",        icon: Store },
  { label: "Farmers Markets",       icon: ShoppingCart },
  { label: "Restaurants & Cafés",   icon: Utensils },
  { label: "Hotels",                icon: Hotel },
  { label: "Caterers",              icon: ChefHat },
  { label: "Bakeries",              icon: Store },
  { label: "Meal Delivery",         icon: Truck },
  { label: "Home Cooks",            icon: ChefHat },
  { label: "Nutritionists & Dietitians", icon: HeartHandshake },
  { label: "Food Scientists",       icon: GraduationCap },
  { label: "Culinary Schools",      icon: GraduationCap },
  { label: "Universities",          icon: GraduationCap },
  { label: "Government Agencies",   icon: Building2 },
  { label: "Food Banks",            icon: HeartHandshake },
  { label: "Nonprofits",            icon: HeartHandshake },
  { label: "Consumers",             icon: Users },
];

const MISSION_BULLETS = [
  "Discover fresh food available nearby",
  "Find real-time deals and discounts on perishable products",
  "Purchase directly from local producers and trusted sellers",
  "Promote surplus food before it expires",
  "Reduce unnecessary food waste",
  "Share recipes, tips, and food knowledge",
  "Make healthier and more informed food choices",
  "Support local agriculture and businesses",
  "Improve food accessibility for communities",
  "Build a more sustainable and efficient global food network",
];

const VISION_BULLETS = [
  "The world's largest food knowledge database",
  "A global directory of food producers and suppliers",
  "Real-time listings for fresh food and special offers",
  "AI-powered recommendations for healthier eating",
  "Food waste reduction through instant surplus notifications",
  "Local food discovery based on location and season",
  "A trusted platform for recipes, nutrition, and culinary inspiration",
  "Global collaboration among consumers, businesses, researchers, and communities",
];

const WASTE_BULLETS = [
  "Alert consumers to discounted food before expiration",
  "Promote surplus inventory from retailers and restaurants",
  "Help farmers market excess harvests",
  "Encourage donations to food banks and charities",
  "Improve inventory visibility across the supply chain",
  "Educate consumers about proper storage and preservation",
  "Share creative recipes that use seasonal and surplus ingredients",
  "Measure and monitor food waste trends over time",
];

const PROFILE_INCLUDES = [
  "Scientific and common names",
  "Origin and history",
  "Cultivation and harvesting",
  "Seasonal availability",
  "Nutrition facts",
  "Health benefits",
  "Storage and food safety",
  "Preparation and cooking tips",
  "Dozens of recipes",
  "Educational videos",
  "Global production statistics",
  "Market outlook",
  "Transportation and supply chain information",
  "Sustainability metrics",
  "Food waste index",
  "Regional varieties",
  "Cultural significance",
  "Community reviews and recommendations",
];

const FUTURE_BULLETS = [
  "Fresh food is easier to discover.",
  "Families spend less on groceries.",
  "Farmers earn more from their harvests.",
  "Businesses reduce waste and increase sales.",
  "Communities have better access to nutritious food.",
  "Fewer edible foods end up in landfills.",
  "The global food system becomes more transparent, resilient, and sustainable.",
];

function SectionHeader({ id, kicker, title, children }) {
  return (
    <div id={id} className="scroll-mt-24">
      <div
        className="text-[10px] font-mono uppercase tracking-widest mb-2"
        style={{ color: "#B25D38" }}
      >
        {kicker}
      </div>
      <h2 className="font-display text-2xl md:text-3xl leading-tight mb-4" style={{ color: "#4A2E0F" }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

export default function FoodPulseAbout() {
  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="fp-about">
      <SeoHead
        section="food-pulse"
        title="About Food Pulse — Connecting the World Through Food"
        description="Food Pulse is a global food intelligence, discovery, and community platform documenting every food product while connecting everyone in the food supply chain in real time."
      />

      {/* Hero */}
      <section style={{ background: "#F0E2C7" }} className="border-b" data-testid="fp-about-hero">
        <div className="max-w-4xl mx-auto px-4 py-14 md:py-20">
          <FoodPulseSlogan surface="about" size="lg" testId="fp-about-slogan" />
          <h1
            className="mt-3 font-display text-4xl md:text-6xl leading-[1.05] tracking-tight"
            style={{ color: "#4A2E0F" }}
          >
            About Food Pulse
          </h1>
          <p
            className="mt-4 text-lg md:text-xl leading-relaxed max-w-3xl"
            style={{ color: "#6B4A16" }}
            data-testid="fp-about-tagline"
          >
            Food Pulse is a global food intelligence, discovery, and community
            platform dedicated to documenting every food product while
            connecting everyone in the food supply chain in real time.
          </p>

          {/* All 5 slogans surface as a rotating strip — reinforcing the
              positioning in case a reader lands mid-page. */}
          <div className="mt-6 flex flex-wrap gap-2" data-testid="fp-about-slogan-strip">
            {Object.values(FP_SLOGANS).map((line) => (
              <span
                key={line}
                className="text-[11px] font-mono px-3 py-1 rounded-full"
                style={{ background: "#fff", color: "#8B5E10", border: "1px solid #E9D8B4" }}
              >
                {line}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-4 py-10 md:py-14 space-y-5 text-base md:text-lg leading-relaxed" style={{ color: "#3E2A0A" }} data-testid="fp-about-intro">
        <p>
          Our vision extends beyond creating the world's most comprehensive
          food encyclopedia. Food Pulse brings together consumers, farmers,
          fishermen, growers, wholesalers, distributors, grocery stores,
          restaurants, food processors, delivery services, food banks,
          researchers, nutritionists, chefs, and local communities into one
          connected ecosystem where knowledge, opportunities, and surplus
          food can be shared instantly.
        </p>
        <p>
          Every day, millions of people search for fresh food, while millions
          of pounds of perfectly edible food are discounted, discarded, or
          wasted due to limited visibility and poor coordination. Food Pulse
          aims to bridge this gap by making food information and food
          availability visible in real time.
        </p>
        <p>
          Whether you are looking for nutritional information about
          blueberries, searching for recipes using tomatoes, finding locally
          harvested peaches, discovering discounted produce near you, or
          helping rescue surplus food before it goes to waste, Food Pulse
          connects information with action.
        </p>
      </section>

      {/* Mission */}
      <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <div className="max-w-5xl mx-auto px-4 py-10 md:py-14" id="vision-mission">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>
            — Vision & Mission
          </div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight mb-6" style={{ color: "#4A2E0F" }}>
            One vision. One mission. One connected food world.
          </h2>

          {/* Big infographic — the user-supplied Vision & Mission illustration */}
          <div
            className="mb-8 rounded-2xl overflow-hidden border"
            style={{ borderColor: "#E9D8B4", background: "#FBF7F0" }}
            data-testid="fp-about-vm-illustration"
          >
            <img
              src="/assets/foodpulse/foodpulse-vision-mission.png"
              alt="Food Pulse Vision & Mission — a connected food ecosystem from farm to table"
              loading="lazy"
              className="w-full h-auto object-contain"
              style={{ background: "#F5EBD5" }}
            />
          </div>

          <h3 className="font-display text-xl md:text-2xl mb-3" style={{ color: "#4A2E0F" }}>Our Vision</h3>
          <div className="space-y-4 text-base md:text-lg leading-relaxed" style={{ color: "#3E2A0A" }} data-testid="fp-about-vision-copy">
            <p>
              At Food Pulse, we envision a world where every food product is
              connected, every opportunity is visible, and no edible food goes
              to waste.
            </p>
            <p>
              Our vision is to build the world's most comprehensive food
              intelligence and discovery platform — bringing together
              knowledge, technology, and community to create a transparent,
              efficient, and sustainable global food ecosystem. From farms
              and fisheries to grocery stores, restaurants, food banks, and
              households, Food Pulse connects every stage of the food journey
              in real time.
            </p>
            <p>
              We aspire to become the trusted destination for everything
              related to food. Every fruit, vegetable, herb, grain, dairy
              product, seafood, and other perishable food will have a rich
              profile featuring its origin, cultivation, nutrition, health
              benefits, storage, sustainability, recipes, market insights,
              and cultural significance. At the same time, users will be
              able to discover local food, compare prices, find special
              offers, access seasonal produce, and explore new culinary
              experiences.
            </p>
            <p>
              Through innovation, artificial intelligence, and community
              collaboration, Food Pulse aims to help consumers make informed
              choices, enable businesses to reach more customers, support
              farmers with greater market visibility, and empower communities
              to reduce food waste and improve food security.
            </p>
            <p>
              Our vision is a future where fresh, nutritious food is easier
              to find, more affordable for everyone, and valued throughout
              its entire journey — from farm to table. By connecting people,
              businesses, and information, Food Pulse is creating a world
              where healthier eating, smarter purchasing, sustainable food
              systems, and responsible consumption become part of everyday
              life.
            </p>
            <p className="italic font-display text-lg md:text-xl" style={{ color: "#6B4A16" }}>
              Together, we are building a future where every food has a
              story, every harvest has a market, every family has access to
              better food, and every meal contributes to a healthier planet.
            </p>
          </div>

          <h3 id="mission" className="scroll-mt-24 font-display text-xl md:text-2xl mt-10 mb-3" style={{ color: "#4A2E0F" }}>Our Mission</h3>
          <div className="space-y-4 text-base md:text-lg leading-relaxed" style={{ color: "#3E2A0A" }} data-testid="fp-about-mission-copy">
            <p>
              At Food Pulse, our mission is to connect everyone in the global
              food ecosystem in real time — bringing together farmers,
              growers, producers, distributors, retailers, restaurants, food
              banks, and consumers on a single platform where food,
              knowledge, and opportunities can be shared seamlessly.
            </p>
            <p>
              We believe that better connections create better outcomes. By
              making fresh food easier to discover, highlighting real-time
              deals on perishable products, and promoting surplus food
              before it goes to waste, Food Pulse helps families save money,
              supports local businesses, and reduces the environmental
              impact of food waste.
            </p>
            <p>
              Beyond connecting buyers and sellers, Food Pulse is building
              one of the world's most comprehensive food knowledge platforms —
              documenting every food product with trusted information on
              nutrition, health benefits, cultivation, sustainability,
              storage, recipes, and culinary traditions.
            </p>
            <p>
              Our mission is to create a smarter, more transparent, and more
              sustainable food ecosystem where every participant in the food
              chain benefits, every edible product is valued, and less food
              ends up in landfills.
            </p>
            <p className="italic font-display text-lg md:text-xl" style={{ color: "#6B4A16" }}>
              Together, we can make healthy food more accessible, reduce
              waste, strengthen local communities, and build a future where
              every meal counts.
            </p>
          </div>

          <div className="mt-6">
            <Link
              to="/why-we-build"
              data-testid="link-why-we-build"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border-2"
              style={{ borderColor: "#B25D38", color: "#B25D38", background: "#FFF8E6" }}
            >
              Read the founding manifesto
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "#3E2A0A" }}>
            The mission bullet list below expands on the ten pillars we
            deliver on every day.
          </p>
        </div>
      </section>

      {/* Mission bullets */}
      <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
          <SectionHeader id="mission-bullets" kicker="— Our Ten Pillars" title="Connect everyone in the global food chain, in real time.">
            <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: "#3E2A0A" }}>
              Food Pulse empowers every participant in the food ecosystem to:
            </p>
            <ul className="grid gap-2 md:grid-cols-2 text-sm md:text-base" style={{ color: "#3E2A0A" }} data-testid="fp-about-mission-bullets">
              {MISSION_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#B25D38" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm md:text-base italic" style={{ color: "#6B4A16" }}>
              By increasing visibility across the food supply chain, Food Pulse
              helps save money for consumers, create new sales opportunities
              for businesses, and ensure that more food reaches people's tables
              instead of landfills.
            </p>
          </SectionHeader>
        </div>
      </section>

      {/* Vision */}
      <section className="border-t" style={{ borderColor: "#E9D8B4" }}>
        <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
          <SectionHeader id="vision" kicker="— Our Vision" title="Every food product — discovered, understood, purchased, shared, and enjoyed.">
            <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: "#3E2A0A" }}>
              We envision a world where every food product can be discovered,
              understood, purchased, shared, and enjoyed before it goes to
              waste. Food Pulse strives to become the world's largest living
              food platform — combining comprehensive food knowledge with
              real-time food discovery, local commerce, sustainability
              insights, and community collaboration.
            </p>
            <ul className="grid gap-2 md:grid-cols-2 text-sm md:text-base" style={{ color: "#3E2A0A" }} data-testid="fp-about-vision-bullets">
              {VISION_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#B25D38" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </SectionHeader>
        </div>
      </section>

      {/* Ecosystem — connecting the food chain */}
      <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <div className="max-w-5xl mx-auto px-4 py-10 md:py-14">
          <SectionHeader id="ecosystem" kicker="— Connecting the Entire Food Chain" title="A digital bridge — farm to table, connected in real time.">
            <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: "#3E2A0A" }}>
              Food Pulse serves as a digital bridge connecting every
              participant in the journey from farm to table. Together, these
              participants create a transparent, connected, and responsive
              food ecosystem that benefits everyone.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="fp-about-ecosystem">
              {ECOSYSTEM.map((n) => (
                <div
                  key={n.label}
                  className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                  style={{ background: "#FBF7F0", borderColor: "#E9D8B4", color: "#3E2A0A" }}
                >
                  <n.icon className="w-4 h-4 flex-shrink-0" style={{ color: "#B25D38" }} />
                  <span>{n.label}</span>
                </div>
              ))}
            </div>
          </SectionHeader>
        </div>
      </section>

      {/* Fighting Food Waste */}
      <section className="border-t" style={{ borderColor: "#E9D8B4" }}>
        <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
          <SectionHeader id="waste" kicker="— Fighting Food Waste" title="Turn surplus into supper.">
            <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: "#3E2A0A" }}>
              Food waste is one of the world's greatest environmental and
              economic challenges. Millions of tons of nutritious food are
              lost each year while millions of people struggle with food
              insecurity. Food Pulse is committed to changing this through
              technology and community collaboration.
            </p>
            <ul className="grid gap-2 md:grid-cols-2 text-sm md:text-base" style={{ color: "#3E2A0A" }}>
              {WASTE_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Recycle className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#4B5638" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm md:text-base italic" style={{ color: "#6B4A16" }}>
              Every meal rescued reduces waste, conserves natural resources,
              lowers greenhouse gas emissions, and supports a more sustainable
              future.
            </p>
          </SectionHeader>
        </div>
      </section>

      {/* More than an encyclopedia */}
      <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
          <SectionHeader id="encyclopedia" kicker="— More Than a Food Encyclopedia" title="Every profile combines science, tradition, and community.">
            <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: "#3E2A0A" }}>
              Every food profile on Food Pulse combines scientific research,
              agricultural knowledge, nutritional information, culinary
              traditions, sustainability insights, recipes, market trends, and
              community contributions. Each entry may include:
            </p>
            <ul className="grid gap-x-4 gap-y-1 md:grid-cols-3 text-sm" style={{ color: "#3E2A0A" }}>
              {PROFILE_INCLUDES.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: "#B25D38" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </SectionHeader>
        </div>
      </section>

      {/* Community + AI */}
      <section className="border-t" style={{ borderColor: "#E9D8B4" }}>
        <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
          <SectionHeader id="community" kicker="— Powered by Community & AI" title="Human expertise meets artificial intelligence.">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#3E2A0A" }}>
              Food Pulse combines the expertise of people with the
              capabilities of artificial intelligence. AI helps organize,
              summarize, and connect vast amounts of food knowledge, while our
              global community contributes local expertise, photographs,
              recipes, observations, farming practices, seasonal updates, and
              food discoveries. Together, they create a living, continuously
              improving knowledge base that reflects the diversity and richness
              of the world's food.
            </p>
          </SectionHeader>
        </div>
      </section>

      {/* Building a Smarter Food Future */}
      <section className="border-t" style={{ borderColor: "#E9D8B4", background: "#fff" }}>
        <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
          <SectionHeader id="future" kicker="— Building a Smarter Food Future" title="Better information leads to better decisions.">
            <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: "#3E2A0A" }}>
              When consumers know where food comes from, how nutritious it is,
              where to find the best prices, how to store it properly, and how
              to use every edible ingredient, everyone benefits. By connecting
              knowledge with real-time opportunities, Food Pulse helps create
              a future where:
            </p>
            <ul className="space-y-2 text-sm md:text-base" style={{ color: "#3E2A0A" }}>
              {FUTURE_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: "#4B5638" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-lg md:text-xl italic font-display" style={{ color: "#4A2E0F" }}>
              Food Pulse is more than a website — it's a living platform that
              connects people, food, knowledge, and opportunity to create a
              healthier, more sustainable world, one meal at a time.
            </p>
          </SectionHeader>
        </div>
      </section>

      {/* Sub-nav placeholders for Team / Partners / Volunteer / Advertise /
          Privacy / Terms — deep-linkable anchors so the About dropdown never
          dead-ends, even though full pages are still in progress. */}
      <section className="border-t" style={{ borderColor: "#E9D8B4" }}>
        <div className="max-w-4xl mx-auto px-4 py-10 grid gap-8 md:grid-cols-2">
          <div id="team" className="scroll-mt-24">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>— Team</div>
            <h3 className="font-display text-xl mb-2" style={{ color: "#4A2E0F" }}>The people behind Food Pulse</h3>
            <p className="text-sm" style={{ color: "#3E2A0A" }}>
              A distributed team of food-lovers, engineers, dietitians, and
              community organizers building the world's living food network.
              A full team page is coming soon — until then, reach out via the
              contact link at the bottom of any page.
            </p>
          </div>
          <div id="partners" className="scroll-mt-24">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>— Partners</div>
            <h3 className="font-display text-xl mb-2" style={{ color: "#4A2E0F" }}>Growing our ecosystem together</h3>
            <p className="text-sm" style={{ color: "#3E2A0A" }}>
              We collaborate with farms, food banks, universities, and
              community groups worldwide. Interested in partnering?
              <Link to="/food-pulse/about#advertise" className="underline ml-1" style={{ color: "#8B5E10" }}>
                Get in touch
              </Link>.
            </p>
          </div>
          <div id="volunteer" className="scroll-mt-24">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>— Volunteer</div>
            <h3 className="font-display text-xl mb-2" style={{ color: "#4A2E0F" }}>Add your local knowledge</h3>
            <p className="text-sm" style={{ color: "#3E2A0A" }}>
              Contribute recipes, market listings, growing tips, or
              translations. Every entry helps the platform serve your
              community better.
            </p>
          </div>
          <div id="advertise" className="scroll-mt-24">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>— Advertise / Contact</div>
            <h3 className="font-display text-xl mb-2" style={{ color: "#4A2E0F" }}>Reach a food-first audience</h3>
            <p className="text-sm" style={{ color: "#3E2A0A" }}>
              Farms, restaurants, retailers, and food-tech partners can promote
              their listings and offers on Food Pulse. Contact us at
              <a className="underline ml-1" style={{ color: "#8B5E10" }} href="mailto:hello@foodpulse.net">
                hello@foodpulse.net
              </a>.
            </p>
          </div>
          <div id="privacy" className="scroll-mt-24">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>— Privacy</div>
            <h3 className="font-display text-xl mb-2" style={{ color: "#4A2E0F" }}>Your data, your control</h3>
            <p className="text-sm" style={{ color: "#3E2A0A" }}>
              See our <Link to="/policies/foodpulse-privacy-policy" className="underline" style={{ color: "#8B5E10" }} data-testid="fp-about-privacy-link">Privacy Policy</Link> for a
              full breakdown of what we collect, why, and how you can request
              deletion at any time.
            </p>
          </div>
          <div id="terms" className="scroll-mt-24">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>— Terms</div>
            <h3 className="font-display text-xl mb-2" style={{ color: "#4A2E0F" }}>Rules of the road</h3>
            <p className="text-sm" style={{ color: "#3E2A0A" }}>
              See our <Link to="/food-pulse/legal/terms" className="underline" style={{ color: "#8B5E10" }} data-testid="fp-about-terms-link">Terms of Service</Link> for
              the community guidelines and platform rules that keep Food Pulse
              trustworthy and useful.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
